import { useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, Bell, Building2, CreditCard, Gauge, Puzzle, User, Users } from "lucide-react";
import { SidebarBody, type NavSection, useSidebar } from "@strivelabs/ui-blocks/v2";
import { cn } from "@strivelabs/ui-core/lib/utils";
import { useIsStrivelabsUser } from "@/hooks/useIsStrivelabsUser";

/**
 * The settings navigation, shown in place of the Workspace/Chat tabs while the
 * user is under `/ui/settings`. The "Back to app" row is a full-width button
 * rendered above the tree (rather than a tree node) so it uses the whole width
 * and keeps its always-black label, instead of inheriting the tree's
 * disclosure-spacer indent and muted row colour. The destinations below render
 * through `SidebarBody`, which handles active state and the collapsed rail.
 */
export function SettingsPanel() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { collapsed } = useSidebar();
  const sections = useSettingsNav();

  // The referrer is the last non-settings route the user was on, captured as
  // they enter settings so "Back to app" returns them there.
  const referrer = useRef("/ui");
  useEffect(() => {
    if (!pathname.startsWith("/ui/settings")) referrer.current = pathname;
  }, [pathname]);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <button
        type="button"
        onClick={() => navigate(referrer.current)}
        aria-label="Back to app"
        className={cn(
          "mb-2 flex items-center rounded-v2-md py-1.5 text-v2-sm font-medium text-v2-sidebar-foreground transition-[background-color,color,transform]",
          "hover:bg-v2-sidebar-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v2-sidebar-ring",
          "active:scale-[0.98] motion-reduce:active:scale-100",
          collapsed ? "justify-center px-0" : "w-full gap-2 px-2",
        )}
      >
        <ArrowLeft size={16} className="shrink-0" />
        {collapsed ? null : "Back to app"}
      </button>

      <SidebarBody sections={sections} />
    </div>
  );
}

/**
 * The settings destinations, grouped. The "Back to app" row is rendered
 * separately.
 *
 * Billing is staff-only while the pages are not customer-ready, so the section
 * is absent for everyone else rather than disabled — a row that refuses to open
 * advertises a surface a customer cannot reach.
 */
export function useSettingsNav(): NavSection[] {
  const { isStrivelabsUser } = useIsStrivelabsUser();

  return useMemo<NavSection[]>(
    () => [
      {
        id: "settings-account",
        label: "Account",
        items: [
          { id: "settings-profile", label: "User Profile", href: "/ui/settings/profile", icon: <User size={16} /> },
        ],
      },
      {
        id: "settings-organisation",
        label: "Organisation",
        items: [
          { id: "settings-organization", label: "Profile", href: "/ui/settings/organization", icon: <Building2 size={16} /> },
          { id: "settings-members", label: "Members", href: "/ui/settings/members", icon: <Users size={16} /> },
          { id: "settings-connections", label: "Apps & Extensions", href: "/ui/settings/connections", icon: <Puzzle size={16} /> },
          { id: "settings-notifications", label: "Notifications", href: "/ui/settings/notifications", icon: <Bell size={16} /> },
        ],
      },
      ...(isStrivelabsUser
        ? [
            {
              id: "settings-billing",
              label: "Billing",
              items: [
                { id: "settings-billing-usage", label: "Usage & credits", href: "/ui/settings/billing/usage", icon: <Gauge size={16} /> },
                { id: "settings-billing-plan", label: "Plan & billing", href: "/ui/settings/billing/plan", icon: <CreditCard size={16} /> },
              ],
            },
          ]
        : []),
    ],
    [isStrivelabsUser],
  );
}
